//////////////////////////////////////////////////////////////////////////////
//
//  포인트 사용 요청 관리 화면
//
//  작성자: MediInStep Team
//  생성일: 2025-12-21
//
//  설명: 포인트 사용 요청 목록 조회, 페이징 기능
//
//////////////////////////////////////////////////////////////////////////////

import { Component } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";


// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";
import BasicTable from "ScreenLayouts/TableLayout/BasicTableLayout/BasicTable";

// Components
import ApprovePointRequestModal from "./components/ApprovePointRequestModal";
import PointUseRequestDetailModal from "./components/PointUseRequestDetailModal";

// Network
// Network
import { request } from "app/network";
import { useMaterialUIController } from "context";
import { formatLocalDateTime, formatLocalDate, formatLocalTime } from "utils/dateFormatter";

interface PointUseRequestListState {
    requests: any[];
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    loading: boolean;
    modalOpen: boolean;
    selectedRequest: any | null;
    detailModalOpen: boolean;
    selectedDetailRequest: any | null;
    selectedRows: any[];
    isBulkApproving: boolean;
    bulkProgress: string;
    filterStatus: string; // "ALL", "PENDING", "APPROVED", "REJECTED"
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_point_use_request_list_page_size";

// Helper to delay (for better UX during fast bulk updates)
const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

class PointUseRequestListScreenClass extends Component<any, PointUseRequestListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 10;

        this.state = {
            requests: [],
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            loading: false,
            modalOpen: false,
            selectedRequest: null,
            detailModalOpen: false,
            selectedDetailRequest: null,
            selectedRows: [],
            isBulkApproving: false,
            bulkProgress: "",
            filterStatus: "ALL",
        };
    }

    componentDidMount() {
        this.loadRequests();
    }

    loadRequests = async () => {
        try {
            this.setState({ loading: true });

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                RequestStatus: this.state.filterStatus === "ALL" ? "" : this.state.filterStatus,
            };

            const res = await request("PointUseRequest.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const requests = (pagedList.items || []).map((req: any) => {
                    return {
                        id: req.seqId,
                        seqId: req.seqId,
                        crtDt: req.crtDt,
                        userName: req.userName || "-",
                        requestPoint: req.requestPoint?.toLocaleString() || "0",
                        useType: req.useType || "-",
                        requestStatus: req.requestStatus,
                        processedByName: req.processedByName || "-",
                        processedDt: req.processedDt,
                        requestMessage: req.requestMessage || "-",
                        cancelerName: req.cancelerName,
                        cancelDt: req.cancelDt,
                        adminMemo: req.adminMemo || "-",
                        rejectionReason: req.rejectionReason || "-",
                        userPhone: req.userPhone || "-",
                        senderPhone: req.senderPhone || "-",
                        receiverPhone: req.receiverPhone || "-",
                        giftishowProduct: req.giftishowProduct,
                    };
                });

                this.setState({
                    requests,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("포인트 사용 요청 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadRequests();
        });
    };

    handleFilterChange = (event: any) => {
        const filterStatus = event.target.value;
        this.setState({ filterStatus, pageNumber: 1, selectedRows: [] }, () => {
            this.loadRequests();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadRequests();
        });
    };

    handleOpenModal = (request: any) => {
        this.setState({
            modalOpen: true,
            selectedRequest: request,
        });
    };

    handleCloseModal = () => {
        this.setState({
            modalOpen: false,
            selectedRequest: null,
        });
    };

    handleRowClick = (rowData: any) => {
        this.setState({
            detailModalOpen: true,
            selectedDetailRequest: rowData,
        });
    };

    handleCloseDetailModal = () => {
        this.setState({
            detailModalOpen: false,
            selectedDetailRequest: null,
        });
    };

    handleApprovalSuccess = () => {
        this.loadRequests();
    };

    handleCancelRequest = async (requestData: any) => {
        const isConfirmed = window.confirm(
            `선택하신 요청(${requestData.requestPoint} 포인트)의 승인을 취소하시겠습니까?\n취소 시 포인트가 환불되며, 이 작업은 되돌릴 수 없습니다.`
        );
        if (!isConfirmed) return;

        const reason = window.prompt("승인 취소 사유를 입력하세요 (선택 사항):", "관리자 취소");

        try {
            this.setState({ loading: true });

            await request("PointUseRequest.cancel", {
                paramz: {
                    seqId: requestData.seqId,
                    reason: reason || "관리자 취소",
                }
            });

            alert("승인이 성공적으로 취소되었습니다.");
            this.loadRequests();
        } catch (error: any) {
            console.error("승인 취소 실패:", error);
            const errorMessage = error.response?.data?.message || error.message || "알 수 없는 오류가 발생했습니다.";
            alert(`승인 취소 실패: ${errorMessage}`);
            this.setState({ loading: false });
        }
    };

    handleSelectionChange = (selectedRows: any[]) => {
        this.setState({ selectedRows });
    };

    handleBulkApprove = async () => {
        const { selectedRows } = this.state;
        const pendingRequests = selectedRows.filter(
            (row) => row.requestStatus === "PENDING" || row.requestStatus === "POINT_REQ_PENDING"
        );

        if (pendingRequests.length === 0) {
            alert("승인할 수 있는 대기 상태의 요청이 없습니다.");
            return;
        }

        const totalPoints = pendingRequests.reduce((sum, row) => sum + (parseInt(row.requestPoint.replace(/,/g, ""), 10) || 0), 0);

        // const confirmMsg = `선택한 ${pendingRequests.length}건의 요청을 일괄 승인하시겠습니까?\n\n총 승인 포인트: ${totalPoints.toLocaleString()} P\n\n(이미 처리된 요청은 제외됩니다)`;
        // if (!window.confirm(confirmMsg)) return;

        this.setState({ isBulkApproving: true, bulkProgress: `0 / ${pendingRequests.length}` });

        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < pendingRequests.length; i++) {
            const req = pendingRequests[i];
            this.setState({ bulkProgress: `${i + 1} / ${pendingRequests.length}` });

            try {
                // Call Approve API
                await request("PointUseRequest.approve", {
                    paramz: {
                        seqId: req.seqId,
                        adminMemo: "일괄 승인 (Bulk Approve)",
                    },
                    noRetry: true,
                });
                successCount++;
            } catch (error) {
                console.error(`Failed to approve request ${req.seqId}`, error);
                failCount++;
            }
            // Small delay to prevent overwhelming the server/UI
            await delay(100);
        }

        this.setState({ isBulkApproving: false, bulkProgress: "" });
        alert(`일괄 승인 완료.\n성공: ${successCount}건\n실패: ${failCount}건`);
        this.loadRequests();
    };

    render() {
        const { requests, pageSize, loading, totalCount, pageNumber, modalOpen, selectedRequest, detailModalOpen, selectedDetailRequest, selectedRows, isBulkApproving, bulkProgress, filterStatus } = this.state;

        const renderDateCell = (value: string | null) => {
            if (!value || value === "-") return "-";

            return (
                <MDBox lineHeight={1.2}>
                    <MDTypography variant="caption" display="block" color="text" fontWeight="medium">
                        {formatLocalDate(value)}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                        {formatLocalTime(value)}
                    </MDTypography>
                </MDBox>
            );
        };

        const columns = [
            {
                Header: "요청일시",
                accessor: "crtDt",
                align: "center",
                width: "12%",
                Cell: ({ value }: any) => renderDateCell(value)
            },
            { Header: "사용자명", accessor: "userName", align: "left", width: "10%" },
            {
                Header: "요청 포인트",
                accessor: "requestPoint",
                align: "right",
                width: "10%",
                Cell: ({ row }: any) => (
                    <MDTypography variant="h6" fontWeight="bold" color="warning">
                        {row.original.requestPoint}
                    </MDTypography>
                )
            },
            {
                Header: "사용 유형",
                accessor: "useType",
                align: "center",
                width: "8%",
                Cell: ({ value }: any) => {
                    if (value === "POINT_USE_LOCAL_VOUCHER") return "지역화폐";
                    if (value === "POINT_USE_GIFTISHOW") return "기프티쇼";
                    return value;
                }
            },
            {
                Header: "상태",
                accessor: "requestStatus",
                align: "center",
                width: "8%",
                Cell: ({ row }: any) => {
                    const status = row.original.requestStatus;
                    let badgeColor: "warning" | "success" | "error" | "secondary" | "info" = "warning";
                    let badgeText = "대기";

                    if (status === "APPROVED" || status === "POINT_REQ_APPROVED") {
                        badgeColor = "success";
                        badgeText = "승인됨";
                    } else if (status === "SENT") {
                        badgeColor = "success";
                        badgeText = "승인됨";
                    } else if (status === "REJECTED" || status === "POINT_REQ_REJECTED") {
                        badgeColor = "error";
                        badgeText = "거부";
                    } else if (status === "CANCELED" || status === "POINT_REQ_CANCELED" || status === "CANCELLED") { // Handle CANCELLED from backend
                        badgeColor = "secondary";
                        badgeText = "취소됨";
                    } else if (status === "USED") {
                        badgeColor = "info"; // or dark/primary
                        badgeText = "사용됨";
                    }

                    return (
                        <MDBadge
                            badgeContent={badgeText}
                            color={badgeColor}
                            variant="gradient"
                            size="sm"
                        />
                    );
                }
            },
            {
                Header: "요청",
                accessor: "action",
                align: "center",
                width: "8%",
                Cell: ({ row }: any) => {
                    const status = row.original.requestStatus;
                    const isPending = status === "PENDING" || status === "POINT_REQ_PENDING";
                    const isApproved = status === "APPROVED" || status === "POINT_REQ_APPROVED" || status === "SENT";

                    if (isPending) {
                        return (
                            <MDButton
                                variant="gradient"
                                color="info"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.handleOpenModal(row.original);
                                }}
                                disabled={this.state.selectedRows.length > 0}
                            >
                                승인
                            </MDButton>
                        );
                    }

                    if (isApproved) {
                        return (
                            <MDButton
                                variant="gradient"
                                color="error"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.handleCancelRequest(row.original);
                                }}
                            >
                                취소
                            </MDButton>
                        );
                    }

                    return null;
                }
            },
            { Header: "처리자", accessor: "processedByName", align: "center", width: "10%" },
            {
                Header: "처리일시",
                accessor: "processedDt",
                align: "center",
                width: "12%",
                Cell: ({ value }: any) => renderDateCell(value)
            },
            {
                Header: "사용 쿠폰",
                accessor: "requestMessage",
                align: "left",
                width: "12%",
                Cell: ({ row }: any) => {
                    const { giftishowProduct, requestMessage } = row.original;
                    if (giftishowProduct && giftishowProduct.goodsName) {
                        return (
                            <MDBox display="flex" flexDirection="column">
                                <MDTypography variant="caption" fontWeight="bold">
                                    {giftishowProduct.brandName}
                                </MDTypography>
                                <MDTypography variant="button" fontWeight="regular">
                                    {giftishowProduct.goodsName}
                                </MDTypography>
                            </MDBox>
                        );
                    }
                    return requestMessage;
                }
            },

        ];

        const rows = requests;

        const totalPages = Math.ceil(totalCount / pageSize);
        const pageCountArray = Array.from({ length: totalPages }, (_, i) => i);
        const currentPageIndex = pageNumber - 1;
        const pageTenNumber = Math.floor(currentPageIndex / 10) * 10;

        const paginationCriteria = {
            PageNumber: currentPageIndex,
            PageCount: pageCountArray,
            PageTenNumber: pageTenNumber,
            totalCount: totalCount,
        };

        const filterStatusComponent = (
            <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                <InputLabel>상태 필터</InputLabel>
                <Select
                    label="상태 필터"
                    value={filterStatus}
                    onChange={this.handleFilterChange}
                    sx={{ height: 40 }}
                >
                    <MenuItem value="ALL">전체</MenuItem>
                    <MenuItem value="PENDING">대기 (PENDING)</MenuItem>
                    <MenuItem value="APPROVED">승인됨 (APPROVED)</MenuItem>
                    <MenuItem value="REJECTED">거부 (REJECTED)</MenuItem>
                    <MenuItem value="CANCELED">취소됨 (CANCELED)</MenuItem>
                </Select>
            </FormControl>
        );

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} position="relative">
                    <Card sx={{ "& table": { tableLayout: "fixed", width: "100%" } }}>
                        <MDBox p={3} lineHeight={1}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                <MDBox display={"flex"} alignContent="center">
                                    <Icon fontSize={"medium"}>request_page</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        포인트 사용 요청 관리 ( USE REQUESTS )
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" alignItems="center" gap={2}>

                                    {selectedRows.length > 0 && (
                                        <MDBox display="flex" alignItems="center" gap={3}>
                                            <MDBox textAlign="right" display="flex" flexDirection="column" justifyContent="center">
                                                <MDTypography variant="subtitle2" fontWeight="bold">
                                                    {selectedRows.length}개 선택됨
                                                </MDTypography>
                                                <MDTypography variant="caption" color="text" lineHeight={1}>
                                                    총 {selectedRows
                                                        .reduce((sum, row) => sum + (parseInt(row.requestPoint.replace(/,/g, ""), 10) || 0), 0)
                                                        .toLocaleString()} P
                                                </MDTypography>
                                            </MDBox>
                                            <MDButton
                                                variant="gradient"
                                                color="info"
                                                onClick={this.handleBulkApprove}
                                                disabled={isBulkApproving}
                                            >
                                                {isBulkApproving ? `진행 중... (${bulkProgress})` : "일괄 승인"}
                                            </MDButton>
                                        </MDBox>
                                    )}
                                </MDBox>
                            </MDBox>

                            <MDTypography variant="button" color="text" mt={1}>
                                포인트 사용 요청 목록 (전체: {totalCount}개)
                            </MDTypography>
                        </MDBox>
                        <BasicTable
                            canSearch={false}
                            criteria={paginationCriteria}
                            storePageSize={pageSize}
                            goPage={this.handlePageChange}
                            setStorePageSize={this.handlePageSizeChange}
                            table={{ columns, rows }}
                            onRowClick={this.handleRowClick}
                            isSorted={false}
                            entriesPerPage={{
                                defaultValue: pageSize,
                                entries: [5, 10, 15, 20, 25, 30, 40, 50],
                            }}
                            showTotalEntries={true}
                            noEndBorder
                            enableSelection={true}
                            onSelectionChange={this.handleSelectionChange}
                            isRowSelectable={(row: any) => row.requestStatus === "PENDING" || row.requestStatus === "POINT_REQ_PENDING"}
                            additionalControls={filterStatusComponent as any}
                        />
                        {/* Bulk Action Bar */}
                        {/* Bulk Action Bar Removed - Moved to Header */}

                        {loading && (
                            <MDBox
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor="rgba(0, 0, 0, 0.3)"
                                sx={{ pointerEvents: "none" }}
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        )}
                    </Card>
                </MDBox>
                <Footer />

                <ApprovePointRequestModal
                    open={modalOpen}
                    onClose={this.handleCloseModal}
                    onSuccess={this.handleApprovalSuccess}
                    requestData={selectedRequest}
                />

                <PointUseRequestDetailModal
                    open={detailModalOpen}
                    onClose={this.handleCloseDetailModal}
                    requestData={selectedDetailRequest}
                />
            </DashboardLayout>
        );
    }
}

function PointUseRequestListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    return <PointUseRequestListScreenClass {...props} darkMode={darkMode} />;
}

export default PointUseRequestListScreen;
